import { ArrowRight, LoaderCircle, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { resolvePackageIdFromPlan, resolvePublicPlanFromPackage, resolveSelectedPlan } from "@/lib/onboardingPlans";
import { getReferralDisplayState } from "@/lib/referrals";
import { assertRootDomainOnly } from "@/lib/routeIsolation";

const DRAFT_KEY = "for-agents-onboarding-draft";

const slugifyPortalName = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 32);

export const AgentOnboardingPage = ({ onAuthenticated, resolveUserDashboardPath, user }) => {
  const navigate = useNavigate();
  const { isAgentPath } = useSiteBranding();
  const isRootDomainAllowed = assertRootDomainOnly(isAgentPath);
  const [searchParams] = useSearchParams();
  const requestedPlan = resolvePackageIdFromPlan(searchParams.get("plan") || "pro");
  const referralState = getReferralDisplayState(searchParams.get("ref"));
  const referralCode = referralState.activeCode;
  const [step, setStep] = useState(1);
  const [registering, setRegistering] = useState(false);
  const [verifyingCea, setVerifyingCea] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", cea_registration_number: "", subdomain_slug: "" });

  const reservedSlug = useMemo(() => slugifyPortalName(form.subdomain_slug || form.name) || "your-name", [form.name, form.subdomain_slug]);
  const selectedPlan = resolveSelectedPlan(requestedPlan);

  useSeo({
    title: "Sign Up | SG Prop Portal",
    description: "Create your SG Prop Portal account, verify your CEA number, complete payment, and activate your Signature dashboard instantly.",
  });

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setForm((current) => ({ ...current, ...parsed }));
      }
    } catch {
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {
    }
  }, [form]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "agent" && user.subscription_status === "Active") {
      navigate(resolveUserDashboardPath(user), { replace: true });
      return;
    }

    setForm((current) => ({
      ...current,
      name: current.name || user.name || "",
      email: current.email || user.email || "",
      phone: current.phone || user.phone || "",
      cea_registration_number: current.cea_registration_number || user.cea_registration_number || "",
      subdomain_slug: current.subdomain_slug || user.subdomain_slug || current.name,
    }));

    if (user.onboarding_state === "payment_required") {
      navigate(`/checkout?plan=${resolvePublicPlanFromPackage(user.plan_type || requestedPlan)}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`, { replace: true });
    }
  }, [navigate, referralCode, requestedPlan, resolveUserDashboardPath, user]);

  if (!isRootDomainAllowed) {
    return <Navigate replace to="/" />;
  }

  if (user?.role === "agent" && user.subscription_status === "Active") {
    return <Navigate replace to={resolveUserDashboardPath(user)} />;
  }

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleAccountContinue = (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      toast.error("Complete your account details first.");
      return;
    }
    setStep(2);
  };

  const ensureRegisteredAccount = async () => {
    if (user?.role === "pending_agent" || user?.role === "agent") return user;
    setRegistering(true);
    try {
      const response = await api.post("/auth/agent-onboarding/register", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        cea_registration_number: form.cea_registration_number,
        package_id: selectedPlan.packageId,
        subdomain_slug: reservedSlug,
        referral_code: referralCode,
      });
      onAuthenticated?.(response.data);
      toast.success("Portal account created. Continue to payment to activate it.");
      return response.data.user;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      throw error;
    } finally {
      setRegistering(false);
    }
  };

  const handleVerifyAndContinue = async (event) => {
    event.preventDefault();
    setVerifyingCea(true);
    try {
      await api.post("/auth/agent-onboarding/verify-cea", { cea_registration_number: form.cea_registration_number, email: form.email });
      await ensureRegisteredAccount();
      toast.success("CEA verification complete. Continue to activation.");
      navigate(`/checkout?plan=${selectedPlan.publicPlanId}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to continue to checkout right now"));
    } finally {
      setVerifyingCea(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.16),transparent_24%),linear-gradient(180deg,#fffaf0_0%,#f9f2e6_52%,#f6ecda_100%)] px-6 py-10 text-[#001935]" data-testid="agent-onboarding-page">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-[30px] border border-[#001935]/10 bg-[#FFFAF1] p-6 shadow-[0_30px_90px_rgba(0,25,53,0.1)] md:p-8" data-testid="agent-onboarding-shell">
          <div className="mb-6 rounded-[22px] border border-[#D4AF37]/20 bg-[#fff4dd] px-5 py-4 text-center text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-founding-banner">
            Founding Agent Special: 50% Lifetime Discount Applied.
          </div>
          {referralCode ? (
            <div className="mb-6 rounded-[22px] border border-[#001935]/10 bg-white px-5 py-4 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-referral-banner">
              <p data-testid="agent-onboarding-referral-banner-primary">
                {referralState.isLockedToDifferentCode
                  ? <>This browser is already locked to referral code <span className="text-[#8b6d16]">{referralCode}</span> for 60 days.</>
                  : referralState.isStoredWithoutQuery
                    ? <>This browser currently has first-click referral attribution locked for 60 days: <span className="text-[#8b6d16]">{referralCode}</span></>
                    : <>First-click referral locked for 60 days: <span className="text-[#8b6d16]">{referralCode}</span></>}
              </p>
              {referralState.isLockedToDifferentCode ? (
                <p className="mt-2 text-xs font-medium leading-6 text-[#5e6e82]" data-testid="agent-onboarding-referral-banner-secondary">
                  This link used <span className="font-semibold text-[#8b6d16]">{referralState.incomingCode}</span>, but it will not override the existing lock in this browser. Use a fresh browser profile or device to test a different partner code.
                </p>
              ) : referralState.isStoredWithoutQuery ? (
                <p className="mt-2 text-xs font-medium leading-6 text-[#5e6e82]" data-testid="agent-onboarding-referral-banner-secondary">
                  If you are testing a new partner link, open it in a fresh browser profile or device so the first-click lock does not carry over from an earlier visit.
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D4AF37]" data-testid="agent-onboarding-kicker">Self-Onboarding Flow</p>
              <h1 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]" data-testid="agent-onboarding-heading">Account Creation → CEA Verification → Checkout Handoff</h1>
            </div>
            <Link className="outline-button border-[#D4AF37]/40 text-[#001935] hover:bg-[#fff4d8]" data-testid="agent-onboarding-back-link" to="/">
              Back to Homepage
            </Link>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {[
              { number: "01", label: "Create Account", active: step >= 1 },
              { number: "02", label: "Verify CEA", active: step >= 2 },
              { number: "03", label: "Proceed to Checkout", active: false },
            ].map((item) => (
              <div className={`rounded-[22px] border px-4 py-4 ${item.active ? "border-[#D4AF37] bg-[#fff4dd]" : "border-[#001935]/10 bg-[#f5efe2]"}`} data-testid={`agent-onboarding-step-${item.number}`} key={item.number}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Step {item.number}</p>
                <p className="mt-2 text-lg font-semibold text-[#001935]">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-5 rounded-[28px] border border-[#001935]/10 bg-[#f4ecdf] p-5" data-testid="agent-onboarding-summary-card">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Reserved portal identity</p>
              <div className="inline-flex items-center rounded-full border border-[#D4AF37]/30 bg-[#fff4dd] px-4 py-2 text-sm font-semibold text-[#001935]" data-testid={`agent-onboarding-plan-selected-${selectedPlan.publicPlanId}`}>
                Selected Plan: {selectedPlan.title}
              </div>
              <p className="break-all text-2xl font-semibold text-[#001935]" data-testid="agent-onboarding-reserved-url">sgpropportal.com/agent/{reservedSlug}</p>
              <p className="text-sm leading-8 text-[#42566f]">Once payment clears, this portal activates immediately and routes you straight into your Signature workspace.</p>
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fff8ee] p-4" data-testid="agent-onboarding-paywall-card">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]">Pay-to-play activation</p>
                <p className="mt-3 text-sm leading-7 text-[#42566f]">There is no free trial in this launch flow. Subscription payment is the gatekeeper for dashboard access.</p>
              </div>
            </div>

            <div className="space-y-6">
              {step <= 1 && !user ? (
                <form className="space-y-4 rounded-[28px] border border-[#001935]/10 bg-[#f4ecdf] p-6" data-testid="agent-onboarding-account-form" onSubmit={handleAccountContinue}>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Step 1</p>
                  <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]">Create your account</h2>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-name-label">Full name
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-name-input" onChange={(event) => updateField("name", event.target.value)} placeholder="Full name" required value={form.name} />
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-email-label">Email address
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-email-input" onChange={(event) => updateField("email", event.target.value)} placeholder="Email address" required type="email" value={form.email} />
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-phone-label">Phone number
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-phone-input" onChange={(event) => updateField("phone", event.target.value)} placeholder="Phone number" required value={form.phone} />
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-password-label">Password
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-password-input" minLength={8} onChange={(event) => updateField("password", event.target.value)} placeholder="Password" required type="password" value={form.password} />
                  </label>
                  <button className="gold-button w-full" data-testid="agent-onboarding-account-continue-button" type="submit">
                    Continue to CEA Verification <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </form>
              ) : null}

              {step >= 2 && user?.role !== "agent" ? (
                <form className="space-y-4 rounded-[28px] border border-[#001935]/10 bg-[#f4ecdf] p-6" data-testid="agent-onboarding-cea-form" onSubmit={handleVerifyAndContinue}>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Step 2</p>
                  <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]">Verify your CEA and portal URL</h2>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-cea-label">CEA registration number
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-cea-input" onChange={(event) => updateField("cea_registration_number", event.target.value.toUpperCase())} placeholder="CEA registration number" required value={form.cea_registration_number} />
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-[#001935]" data-testid="agent-onboarding-slug-label">Preferred portal URL slug
                    <input className="w-full rounded-[18px] border border-[#001935]/14 bg-white px-4 py-4 text-[#001935] outline-none placeholder:text-[#6b7280] focus:border-[#D4AF37]" data-testid="agent-onboarding-slug-input" onChange={(event) => updateField("subdomain_slug", event.target.value)} placeholder="Preferred portal URL slug" value={form.subdomain_slug} />
                  </label>
                  <div className="rounded-[22px] border border-[#001935]/10 bg-[#fff8ee] px-4 py-4" data-testid="agent-onboarding-slug-preview-card">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Your reserved URL</p>
                    <p className="mt-2 break-all text-lg font-semibold text-[#001935]" data-testid="agent-onboarding-slug-preview-url">sgpropportal.com/agent/{reservedSlug}</p>
                  </div>
                  <button className="gold-button w-full" data-testid="agent-onboarding-cea-verify-button" disabled={verifyingCea} type="submit">
                    {verifyingCea ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                    Verify and Continue to Checkout
                  </button>
                </form>
              ) : null}

              <div className="rounded-[28px] border border-[#001935]/10 bg-[#fff8ee] p-6" data-testid="agent-onboarding-next-step-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Step 3</p>
                <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#001935]">Payment happens on `/checkout`.</h2>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">Your selected plan stays attached from the homepage CTA to the final payment step so activation is ready for confirmation.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};